/**
 * AppleSSOButton — standalone Apple Sign-In button.
 * =================================================
 *
 * TREATX-APPLE-OAUTH-FRONTEND-01.
 *
 * Rendered "coming soon" / disabled when `REACT_APP_APPLE_CLIENT_ID`
 * is not set. Otherwise opens the Apple Sign-In popup via the official
 * Sign in with Apple JS SDK, returns `{ id_token, name }` to the parent
 * via `onSuccess`, or `err` via `onError` on cancel / failure.
 *
 * Never logs id_token, name, or authorization code.
 */
import React, { useState } from 'react';
import {
  signInWithApple,
  isAppleAuthConfigured,
} from '../services/appleAuth';

const _AppleIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const AppleSSOButton = ({
  onSuccess,
  onError,
  loading = false,
  testId = 'apple-login-btn',
  size = 'default', // 'default' | 'small'
}) => {
  const [busy, setBusy] = useState(false);
  const enabled = isAppleAuthConfigured();

  const onClick = async () => {
    if (!enabled || busy || loading) return;
    setBusy(true);
    try {
      const result = await signInWithApple();
      if (onSuccess) await onSuccess(result);
    } catch (err) {
      if (onError) onError(err);
    } finally {
      setBusy(false);
    }
  };

  const heightCls = size === 'small' ? 'h-9 text-xs' : 'h-10 text-sm';

  const enabledCls =
    `w-full flex items-center justify-center gap-2.5 ${heightCls} px-4 ` +
    'bg-black hover:bg-slate-900 border border-black rounded-lg ' +
    'cursor-pointer font-medium text-white transition-colors ' +
    'disabled:opacity-60 disabled:cursor-not-allowed';

  const disabledCls =
    `w-full flex items-center justify-center gap-2.5 ${heightCls} px-4 ` +
    'bg-white border border-slate-200 rounded-lg opacity-40 ' +
    'cursor-not-allowed font-medium text-slate-500';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!enabled || busy || loading}
      className={enabled ? enabledCls : disabledCls}
      data-testid={testId}
      title={enabled ? 'Sign in with Apple' : 'Apple Sign-In — coming soon'}
    >
      <_AppleIcon className={size === 'small' ? 'w-4 h-4' : 'w-5 h-5'} />
      <span className="ml-2">
        {enabled
          ? (busy ? 'Signing in…' : 'Sign in with Apple')
          : 'Apple Sign-In (coming soon)'}
      </span>
    </button>
  );
};

export default AppleSSOButton;
